import React from 'react';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';

const AuthorPage = () => (
  <main className="pt-24">
    {/* Hero */}
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="relative">
            <div className="absolute inset-0 bg-brand-200 rounded-2xl transform translate-x-4 translate-y-4"></div>
            <img
              src="https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/91ab01df2fc17bd64fc03588a793bcb6.jpg"
              alt="Andrei Marius Ciomîrtan - Author"
              className="relative z-10 rounded-2xl shadow-xl w-full object-cover aspect-[4/5]"
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <span className="text-brand-600 font-semibold tracking-widest uppercase text-sm mb-4 block">About the Author</span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">Andrei Marius Ciomîrtan</h1>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">
              Andrei Marius Ciomîrtan is a passionate observer of human connections and relationship dynamics. With years of experience listening to couples navigate the complexities of communication, Andrei discovered a recurring pattern: the profound disconnect between how men express love and how women expect to receive it.
            </p>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">
              His work focuses on bridging this gap, translating the silent, often overlooked gestures of devotion into a language that brings clarity and deeper intimacy to relationships.
            </p>
            <div className="p-5 bg-stone-100 rounded-xl border border-stone-200 flex items-start gap-4">
              <div className="w-1 self-stretch bg-brand-400 rounded-full flex-shrink-0"></div>
              <p className="text-stone-700 italic font-serif text-lg leading-relaxed">
                "The most romantic thing a person can do is stay — fully present, fully committed, day after day."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Vision */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">The Vision Behind the Book</h2>
          <p className="text-lg text-stone-600">Why Andrei wrote The Silent Language of Love</p>
        </motion.div>
        <div className="space-y-6">
          {[
            { title: "A Pattern He Couldn't Ignore", text: "After years of observing couples and listening to their struggles, one theme kept surfacing: men and women often speak different love languages — not because they don't care, but because they were never taught to read each other's dialect." },
            { title: "The Gap He Wanted to Close", text: "Women often feel unloved when love is actually all around them, expressed in ways they weren't taught to recognize. Men often feel unseen when their acts of service and protection go unnoticed. This book was born to close that gap." },
            { title: "A New Definition of Romance", text: "Andrei's mission is simple: help people see that romance doesn't have to be loud to be real. The most enduring love is often quiet, consistent, and built in the everyday moments most of us overlook." },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="p-8 bg-stone-50 rounded-2xl border border-stone-100">
              <h3 className="text-xl font-serif font-bold text-stone-900 mb-3">{item.title}</h3>
              <p className="text-stone-600 leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Quote Banner */}
    <section className="py-16 bg-stone-900 text-white text-center">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-2xl md:text-3xl font-serif italic leading-relaxed">
          "Love is not always spoken. Sometimes it is built, carried, protected, and offered — silently, every single day."
        </p>
        <p className="mt-6 text-stone-400 font-semibold">— Andrei Marius Ciomîrtan</p>
      </div>
    </section>

    <div id="get-copy"><CallToAction /></div>
  </main>
);

export default AuthorPage;